import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #0a0e1a;
    --surface: #111827;
    --surface2: #1a2235;
    --border: rgba(255,255,255,0.07);
    --accent: #00d4ff;
    --accent2: #7b5ea7;
    --success: #22c55e;
    --text: #e8edf5;
    --muted: #6b7a99;
    --font-display: 'Syne', sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
  }

  body {
    background: var(--bg);
    color: var(--text);
    font-family: var(--font-display);
    min-height: 100vh;
  }

  .dashboard {
    min-height: 100vh;
    background: radial-gradient(ellipse at 20% 50%, rgba(0,212,255,0.04) 0%, transparent 60%),
                radial-gradient(ellipse at 80% 20%, rgba(123,94,167,0.06) 0%, transparent 50%),
                var(--bg);
  }

  .nav {
    padding: 20px 48px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(10,14,26,0.85);
    backdrop-filter: blur(12px);
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .nav-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 20px;
    font-weight: 800;
    color: var(--text);
    cursor: pointer;
  }

  .nav-logo span { font-size: 26px; }
  .nav-right { display: flex; align-items: center; gap: 16px; }

  .nav-email {
    font-size: 13px;
    color: var(--muted);
    font-family: var(--font-mono);
  }

  .btn-signout {
    background: transparent;
    color: var(--muted);
    border: 1px solid var(--border);
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    font-family: var(--font-display);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-signout:hover { color: var(--text); border-color: rgba(255,255,255,0.2); }

  .main { max-width: 900px; margin: 0 auto; padding: 56px 24px; }

  .welcome { margin-bottom: 48px; }

  .welcome-eyebrow {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--accent);
    letter-spacing: 0.15em;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  .welcome-title { font-size: 32px; font-weight: 800; color: var(--text); margin-bottom: 8px; }
  .welcome-sub { font-size: 15px; color: var(--muted); line-height: 1.6; }

  .success-banner {
    background: rgba(34,197,94,0.1);
    border: 1px solid rgba(34,197,94,0.2);
    border-radius: 12px;
    padding: 16px 20px;
    margin-bottom: 32px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: var(--success);
    font-weight: 600;
  }

  /* ── Filter row ── */
  .filter-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--muted);
    font-family: var(--font-mono);
    letter-spacing: 0.1em;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  /* Desktop pills — hidden on mobile */
  .filter-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: flex-end;
  }

  .filter-pill {
    font-family: var(--font-mono);
    font-size: 11px;
    padding: 5px 12px;
    border-radius: 100px;
    border: 1px solid var(--border);
    background: transparent;
    color: var(--muted);
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }

  .filter-pill:hover {
    border-color: rgba(255,255,255,0.15);
    color: var(--text);
  }

  .filter-pill.active {
    background: rgba(0,212,255,0.1);
    border-color: rgba(0,212,255,0.35);
    color: var(--accent);
  }

  /* Mobile select — hidden on desktop */
  .filter-select-wrap {
    display: none;
    position: relative;
  }

  .filter-select {
    appearance: none;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 7px 32px 7px 12px;
    font-size: 12px;
    font-family: var(--font-mono);
    color: var(--text);
    cursor: pointer;
    min-width: 160px;
  }

  .filter-select:focus { outline: none; border-color: rgba(0,212,255,0.4); }

  .filter-select-chevron {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--muted);
    font-size: 11px;
  }

  @media (max-width: 600px) {
    .filter-pills { display: none; }
    .filter-select-wrap { display: block; }
    .filter-row { flex-wrap: wrap; gap: 10px; }
    .nav { padding: 16px 20px; }
    .nav-email { display: none; }
    .main { padding: 40px 20px; }
  }

  /* ── Exam grid ── */
  .exams-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
    margin-bottom: 56px;
  }

  .exams-empty {
    grid-column: 1 / -1;
    text-align: center;
    padding: 48px 24px;
    color: var(--muted);
    font-size: 14px;
    font-family: var(--font-mono);
    border: 1px dashed var(--border);
    border-radius: 16px;
  }

  .exam-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
    overflow: hidden;
    transition: all 0.2s;
  }

  .exam-card.purchased { border-color: rgba(34,197,94,0.2); cursor: pointer; }

  .exam-card.purchased::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--success), #16a34a);
  }

  .exam-card.purchased:hover {
    transform: translateY(-2px);
    border-color: rgba(34,197,94,0.4);
    box-shadow: 0 8px 24px rgba(34,197,94,0.08);
  }

  .exam-card.unpurchased { cursor: default; opacity: 0.85; }
  .exam-card.coming-soon { opacity: 0.5; }

  .exam-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-mono);
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 100px;
    letter-spacing: 0.05em;
    width: fit-content;
  }

  .badge-purchased { background: rgba(34,197,94,0.1); color: var(--success); border: 1px solid rgba(34,197,94,0.2); }
  .badge-available { background: rgba(0,212,255,0.08); color: var(--accent); border: 1px solid rgba(0,212,255,0.2); }
  .badge-soon { background: rgba(255,255,255,0.04); color: var(--muted); border: 1px solid var(--border); }

  .exam-name { font-size: 17px; font-weight: 700; color: var(--text); line-height: 1.4; }

  .exam-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .exam-questions { font-size: 13px; color: var(--muted); font-family: var(--font-mono); }

  .exam-price {
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
  }

  .exam-price sup { font-size: 11px; vertical-align: super; }
  .exam-price span { font-size: 11px; color: var(--muted); font-weight: 400; margin-left: 2px; }

  .btn-start {
    display: block;
    width: 100%;
    background: var(--success);
    color: #0a0e1a;
    border: none;
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 800;
    font-family: var(--font-display);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: center;
  }

  .btn-start:hover { background: #16a34a; transform: translateY(-1px); }

  .btn-buy {
    display: block;
    width: 100%;
    background: transparent;
    color: var(--accent);
    border: 1px solid rgba(0,212,255,0.3);
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 700;
    font-family: var(--font-display);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: center;
  }

  .btn-buy:hover { background: rgba(0,212,255,0.08); border-color: rgba(0,212,255,0.5); }

  /* ── Coming soon modal ── */
  .modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    backdrop-filter: blur(4px);
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.15s ease;
  }

  .modal {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 20px;
    padding: 40px;
    width: 420px;
    max-width: 90vw;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6);
    animation: fadeUp 0.2s ease;
    text-align: center;
  }
/* FOOTER */
  .footer {
    border-top: 1px solid var(--border);
    padding: 32px 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
  }

  .footer-logo { font-size: 16px; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
  .footer-links { display: flex; gap: 24px; align-items: center; }

  .footer-link {
    font-size: 13px;
    color: var(--muted);
    background: none;
    border: none;
    cursor: pointer;
    font-family: var(--font-display);
    transition: color 0.2s;
    text-decoration: none;
  }

  .footer-link:hover { color: var(--text); }
  .footer-copy { font-size: 12px; color: var(--muted); font-family: var(--font-mono); }

  
  .modal-icon { font-size: 40px; margin-bottom: 16px; }
  .modal-title { font-size: 18px; font-weight: 700; margin-bottom: 10px; color: var(--text); }
  .modal-body { font-size: 14px; color: var(--muted); line-height: 1.7; margin-bottom: 24px; }
  .modal-close-btn {
    background: var(--surface2);
    color: var(--text);
    border: 1px solid var(--border);
    padding: 10px 28px;
    font-size: 14px;
    font-weight: 600;
    font-family: var(--font-display);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .modal-close-btn:hover { border-color: rgba(255,255,255,0.2); }

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

  .loading { display: flex; align-items: center; justify-content: center; min-height: 60vh; color: var(--muted); font-family: var(--font-mono); font-size: 13px; }
`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [exams, setExams] = useState([]);
  const [questionCounts, setQuestionCounts] = useState({});

  // Filter state
  const [activeFilter, setActiveFilter] = useState("All");
  const [examTypes, setExamTypes] = useState([]);

  const fetchExams = async () => {
    const { data } = await supabase
      .from("exams")
      .select("id, name, slug, price, active, exam_type")
      .order("id", { ascending: true });
    if (data) {
      setExams(data);
      // Derive unique exam types, preserving order of first appearance
      const types = ["All", ...new Set(data.map(e => e.exam_type).filter(Boolean))];
      setExamTypes(types);
    }
    return data || [];
  };

  const fetchPurchases = async (userId) => {
    const { data: purchaseData } = await supabase
      .from("purchases")
      .select("exam_id")
      .eq("user_id", userId);
    setPurchases(purchaseData?.map(p => Number(p.exam_id)) || []);
  };

  const fetchQuestionCounts = async (examList) => {
    const results = await Promise.all(
      examList.map(async (exam) => {
        const { count } = await supabase
          .from("questions")
          .select("*", { count: "exact", head: true })
          .eq("exam_id", exam.id);
        return [exam.id, count];
      })
    );
    setQuestionCounts(Object.fromEntries(results));
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSuccess = params.get("success") === "true";
    if (isSuccess) {
      setShowSuccess(true);
      window.history.replaceState({}, "", "/dashboard");
    }

    fetchExams().then(examList => {
      fetchQuestionCounts(examList);
    });

    supabase.auth.getSession().then(async ({ data }) => {
      const s = data.session;
      setSession(s);
      if (s) {
        if (isSuccess) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        await fetchPurchases(s.user.id);
      }
      setLoading(false);
    });
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const getFirstName = () => {
    const meta = session?.user?.user_metadata;
    if (meta?.first_name) return meta.first_name;
    if (meta?.full_name) return meta.full_name.split(" ")[0];
    if (meta?.name) return meta.name.split(" ")[0];
    return session?.user?.email?.split("@")[0] || "there";
  };

  const hasPurchased = (examId) => purchases.includes(examId);

  // Filter exams by active type
  const filteredExams = activeFilter === "All"
    ? exams
    : exams.filter(e => e.exam_type === activeFilter);

  // ── PAYMENTS LIVE: uncomment handleBuyNow and swap the Buy Now button's
  // onClick back from () => setShowComingSoon(true) to () => handleBuyNow(exam)
  
   const handleBuyNow = async (exam) => {
     const { data: { session } } = await supabase.auth.getSession();
     const response = await fetch(
       `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout-session`,
       {
         method: "POST",
         headers: {
           "Content-Type": "application/json",
           "Authorization": `Bearer ${session.access_token}`,
         },
         body: JSON.stringify({
           examId: exam.id,
           examName: exam.name,
           userId: session.user.id,
           userEmail: session.user.email,
         }),
       }
     );
     const { url, error } = await response.json();
     if (error) {
       alert("Something went wrong. Please try again.");
       return;
     }
     window.location.href = url;
   };

  if (loading) {
    return (
      <>
        <style>{styles}</style>
        <div className="dashboard">
          <div className="loading">Loading your dashboard...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="dashboard">

        <nav className="nav">
          <div className="nav-logo" onClick={() => navigate("/")}>
            <span>🐧</span> PassPenguin
          </div>
          <div className="nav-right">
            <span className="nav-email">{session?.user?.email}</span>
            <button className="btn-signout" onClick={handleSignOut}>Sign out</button>
          </div>
        </nav>

        <main className="main">

          <div className="welcome">
            <p className="welcome-eyebrow">Dashboard</p>
            <h1 className="welcome-title">Welcome back, {getFirstName()} 🐧</h1>
            <p className="welcome-sub">
              {purchases.length > 0
                ? `You have access to ${purchases.length} exam${purchases.length > 1 ? "s" : ""}. Ready to practise?`
                : "Purchase an exam below to get started."}
            </p>
          </div>

          {showSuccess && (
            <div className="success-banner">
              ✓ Payment successful! Your exam is now unlocked below.
            </div>
          )}

          {/* Filter row — pills on desktop, select on mobile */}
          <div className="filter-row">
            <p className="section-title">Available exams</p>

            {/* Desktop: pills */}
            {examTypes.length > 1 && (
              <div className="filter-pills" role="group" aria-label="Filter by exam type">
                {examTypes.map(type => (
                  <button
                    key={type}
                    className={`filter-pill${activeFilter === type ? " active" : ""}`}
                    onClick={() => setActiveFilter(type)}
                    aria-pressed={activeFilter === type}
                  >
                    {type}
                    {type !== "All" && (
                      <span style={{ marginLeft: 4, opacity: 0.6 }}>
                        ({exams.filter(e => e.exam_type === type).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Mobile: select */}
            {examTypes.length > 1 && (
              <div className="filter-select-wrap">
                <select
                  className="filter-select"
                  value={activeFilter}
                  onChange={e => setActiveFilter(e.target.value)}
                  aria-label="Filter by exam type"
                >
                  {examTypes.map(type => (
                    <option key={type} value={type}>
                      {type === "All"
                        ? `All exam types (${exams.length})`
                        : `${type} (${exams.filter(e => e.exam_type === type).length})`}
                    </option>
                  ))}
                </select>
                <span className="filter-select-chevron">▾</span>
              </div>
            )}
          </div>

          <div className="exams-grid">
            {filteredExams.length === 0 ? (
              <div className="exams-empty">No exams found for this type.</div>
            ) : (
              filteredExams.map(exam => {
                const purchased = hasPurchased(exam.id);
                const cardClass = `exam-card ${!exam.active ? "coming-soon" : purchased ? "purchased" : "unpurchased"}`;

                return (
                  <div
                    key={exam.id}
                    className={cardClass}
                    onClick={purchased && exam.active ? () => navigate(`/exam/${exam.slug}`) : undefined}
                  >
                    <span className={`exam-badge ${!exam.active ? "badge-soon" : purchased ? "badge-purchased" : "badge-available"}`}>
                      {!exam.active ? "Coming soon" : purchased ? "✓ Purchased" : "Available"}
                    </span>
                    <p className="exam-name">{exam.name}</p>
                    <div className="exam-meta">
                      <span className="exam-questions">
                        {questionCounts[exam.id] != null ? questionCounts[exam.id] : "100+"} questions
                      </span>
                      {!purchased && exam.active && (
                        <span className="exam-price"><sup>$</sup>{exam.price} <span>AUD</span></span>
                      )}
                    </div>
                    {exam.active && (
                      purchased ? (
                        <button className="btn-start" onClick={() => navigate(`/exam/${exam.slug}`)}>
                          Start exam →
                        </button>
                      ) : (
                        <button className="btn-buy" onClick={() => handleBuyNow(exam)}>
                          Buy now
                        </button>
                      )
                    )}
                  </div>
                );
              })
            )}
          </div>

        </main>
        <footer className="footer">
                  <div className="footer-logo">🐧 PassPenguin</div>
                  <div className="footer-links">
                    <a className="footer-link" href="/terms-of-service">Terms of Service</a>
                    <a className="footer-link" href="/privacy-policy">Privacy Policy</a>
                    <a className="footer-link" href="mailto:hello@passpenguin.com">Contact</a>
                  </div>
                  <p className="footer-copy">© {new Date().getFullYear()} PassPenguin</p>
                </footer>
        {showComingSoon && (
          <div className="modal-overlay" onClick={() => setShowComingSoon(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-icon">🚧</div>
              <p className="modal-title">Payments coming soon</p>
              <p className="modal-body">
                We're still setting up payments. Check back soon.
              </p>
              <button className="modal-close-btn" onClick={() => setShowComingSoon(false)}>Got it</button>
            </div>
          </div>
        )}

      </div>
    </>
  );
}